import React, { createContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api';

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
    // سعی می‌کند توکن‌ها را از حافظه محلی بخواند
    const [authTokens, setAuthTokens] = useState(() => localStorage.getItem('authTokens') ? JSON.parse(localStorage.getItem('authTokens')) : null);
    // سعی می‌کند اطلاعات کاربر را از توکن بخواند
    const [user, setUser] = useState(() => localStorage.getItem('authTokens') ? jwtDecode(JSON.parse(localStorage.getItem('authTokens')).access) : null);
    const [loading, setLoading] = useState(true); // State برای مدیریت لود اولیه
    const navigate = useNavigate();

    // فقط وضعیت لوکال را پاک می‌کند — بدون هدایت به صفحه‌ی دیگر.
    // برای پاک‌سازیِ خاموش (بدون توکن معتبر، یا تمدید ناموفق در پس‌زمینه) استفاده می‌شود
    // تا کاربر مهمانی که مثلاً وارد یک مقاله یا پروفایل شده، ناخواسته به صفحه‌ی اصلی پرتاب نشود.
    const clearAuth = useCallback(() => {
        setAuthTokens(null);
        setUser(null);
        localStorage.removeItem('authTokens');
    }, []);

    // خروج صریح کاربر (کلیک روی دکمه‌ی «خروج») — این یکی باید هدایت هم بکند.
    const logoutUser = useCallback(() => {
        clearAuth();
        navigate('/');
    }, [clearAuth, navigate]);

    // تابع loginUser حالا فقط توکن‌ها را می‌گیرد و ذخیره می‌کند
    const loginUser = useCallback((tokens) => {
        try {
            if (tokens && tokens.access) {
                setAuthTokens(tokens);
                const decodedUser = jwtDecode(tokens.access);
                setUser(decodedUser);
                localStorage.setItem('authTokens', JSON.stringify(tokens));
                navigate('/dashboard'); // به داشبورد هدایت کن
            } else {
                console.error("!!! loginUser Error: Invalid or missing tokens received:", tokens);
            }
        } catch (error) {
            console.error("!!! loginUser Error: Failed to decode token or navigate:", error);
            clearAuth();
        }
    }, [navigate, clearAuth]);

    // تابع تمدید خودکار توکن
    const updateToken = useCallback(async () => {
        const currentAuthTokens = JSON.parse(localStorage.getItem('authTokens'));
        if (!currentAuthTokens?.refresh) {
            clearAuth();
            setLoading(false); // لود اولیه تمام شد
            return;
        }

        try {
            const response = await apiClient.post('/api/auth/token/refresh/', {
                refresh: currentAuthTokens.refresh
            });

            if (response.status === 200) {
                const newTokens = response.data;
                const decodedUser = jwtDecode(newTokens.access);
                // ساختن آبجکت توکن کامل شامل رفرش
                const fullTokens = { access: newTokens.access, refresh: currentAuthTokens.refresh }; // رفرش توکن قبلی را نگه می‌داریم

                setAuthTokens(fullTokens);
                setUser(decodedUser);
                localStorage.setItem('authTokens', JSON.stringify(fullTokens));
            } else {
                clearAuth();
            }
        } catch (error) {
            // نشست منقضی شده — فقط وضعیت را پاک می‌کنیم، کاربر را از صفحه‌ی فعلی پرت نمی‌کنیم
            clearAuth();
        }
        if (loading) setLoading(false); // لود اولیه تمام شد

    }, [clearAuth, loading]);

    // useEffect برای تمدید دوره‌ای توکن
    useEffect(() => {
        const interval = setInterval(() => {
            if (authTokens) {
                updateToken();
            }
        }, 1000 * 60 * 4); // 4 minutes
        return () => clearInterval(interval);
    }, [authTokens, updateToken]); // updateToken را به dependency اضافه می‌کنیم

     // useEffect برای بررسی توکن در اولین بارگذاری (برای جلوگیری از لاگین ماندن با توکن نامعتبر)
     useEffect(() => {
        if (loading) {
            updateToken(); // در اولین بارگذاری یک بار توکن را چک/رفرش کن
        }
     }, [loading, updateToken]); // updateToken را به dependency اضافه می‌کنیم


    const contextData = {
        user: user,
        authTokens: authTokens,
        loginUser: loginUser,
        logoutUser: logoutUser,
        loading: loading // ارسال وضعیت لود اولیه به کامپوننت‌ها
    };

    return (
        <AuthContext.Provider value={contextData}>
            {children}
        </AuthContext.Provider>
    );
};
